import { observer } from 'mobx-react';
import { TranslationsContext } from '@teliaee/telia-translate';
import * as React from 'react';
import { Button, IFormHelperProps, List, ListItem, Notice, Separator } from '@teliaee/design-system';
import { ERRORED_HELPER_FIELD_ID_SUFFIX, removeIdIndexFromString } from '../utils/FieldHelperUtil';
import { scrollUpToElement } from '../utils/HtmlUtil';
import { useIsMobileHook } from '../utils/ThemeHooks';

interface FormFieldsErrorNoticeProps {
  erroredFields: (IFormHelperProps | undefined)[];
}

const FormErrorNotice = observer((props: FormFieldsErrorNoticeProps): React.JSX.Element => {
  const { erroredFields } = props;
  const translate = React.useContext(TranslationsContext).translate;
  const { isMobileOrTablet } = useIsMobileHook();

  React.useEffect(() => {
    scrollUpToElement('form-error-notice', isMobileOrTablet);
  }, []);

  if (!erroredFields?.length) {
    return null;
  }

  const erroredFieldIds = erroredFields
    .filter((helper): helper is IFormHelperProps => helper !== undefined && helper !== null)
    .map((helper) => helper?.id)
    .filter((fieldId): fieldId is string => fieldId !== undefined)
    .map((fieldId) => fieldId.replace(ERRORED_HELPER_FIELD_ID_SUFFIX, ''));

  const focusField = (id: string) => {
    document.querySelectorAll(`#${id}`).forEach((element) => {
      // @ts-ignore
      element.offsetParent !== null && element.focus();
    });
  };

  return (
    <Notice id="form-error-notice" type="error" attributes={{ 'aria-live': 'polite', 'aria-atomic': 'true' }}>
      <strong>{translate('form.error')}</strong>
      {erroredFieldIds && erroredFieldIds.length > 0 && (
        <>
          <Separator spacing="xxsmall" color="invisible" />
          <List type="unstyled">
            {erroredFieldIds.map((erroredField) => (
              <ListItem key={`erroredField-${erroredField}-button`} className="text-small">
                <Button
                  text={translate(`errored.field.button.${removeIdIndexFromString(erroredField)}`)}
                  type="withdrawal"
                  link
                  onClick={() => {
                    focusField(erroredField);
                    // Needed for focusing select fields
                    focusField(`${erroredField}-input`);
                  }}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </Notice>
  );
});

export default FormErrorNotice;
